import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteAppPassword } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { AppPassword } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteAppPasswordDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedAppPassword: AppPassword;
  allAppPasswords: AppPassword[];
  changeLogRoles: string[];
}

export default function DeleteAppPasswordDialog({
  open,
  handleClose,
  userEmail,
  selectedAppPassword,
  allAppPasswords,
  changeLogRoles,
}: DeleteAppPasswordDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteAppPassword(userEmail, selectedAppPassword, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'App password deleted successfully!',
      });

      const newList = allAppPasswords.filter(
        (appPassword) => appPassword.codeId !== selectedAppPassword.codeId
      );

      queryClient.setQueryData([UserKeys.AppPasswords, userEmail], newList);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this app password. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.app_passwords
          .delete_app_password,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.appPasswords,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.DeleteAppPasswords.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Delete Password"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            {`Do you wish to delete ${selectedAppPassword.name} from ${userEmail}'s app passwords?`}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
